import React, { useCallback, useEffect, useState } from "react";
import { Spinner } from "../atoms/Spinner";
import { useTranslation } from "react-i18next";
import { HiOutlineChartBar } from "react-icons/hi";
import { UnlockBadge } from "../atoms/UnlockBadge";
import { CustomButton } from "../atoms/CustomButton";
import { EmptyState } from "../atoms/EmptyState";
import { ModalShell } from "../atoms/ModalShell";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
} from "recharts";
import useAuth from "../../hooks/useAuth";
import { toast } from "react-toastify";
import moment from "moment";
import { useUpdateTracker } from "../../hooks/users";
import { AxiosResponse } from "axios";
import { PlanTier, usePermissions } from "../../utils/permissions";
import { palette, chartSeries } from "../../utils/palette";
import UpgradeModal from "./UpgradeModal";
import * as ct from "countries-and-timezones";

interface BusinessCardStatisticsModalProps {
  show: boolean;
  onClose: () => void;
  userIdentification: string;
  userName: string;
}

interface BusinessCardEvent {
  date_time: string;
  value: {
    message: string;
    value: {
      timezone: string;
      language: string;
      platform: string;
      referrer: string;
      browserName: string;
      isMobile: boolean;
    };
  };
  event: string;
}

interface ProcessedStats {
  totalViews: number;
  mobileViews: number;
  desktopViews: number;
  browserStats: { name: string; count: number }[];
  dailyViews: { date: string; views: number }[];
  platformData: { name: string; value: number }[];
  timezoneStats: { name: string; count: number }[];
  countryStats: { name: string; count: number }[];
}

const CHART = {
  grid: palette.gray200,
  axis: palette.gray500,
  tooltipBg: palette.gray800,
  tooltipText: palette.gray50,
  series: palette.blue500,
};

const COLORS = [...chartSeries];

const getMockData = (t: any) => ({
  platformData: [
    { name: t("businessCardPage.statisticsModal.mobile"), value: 847 },
    { name: t("businessCardPage.statisticsModal.desktop"), value: 1253 },
  ],
  browserStats: [
    { name: "Chrome", count: 1456 },
    { name: "Safari", count: 892 },
    { name: "Firefox", count: 387 },
    { name: "Edge", count: 265 },
  ],
  timezoneStats: [
    { name: "America/New_York", count: 543 },
    { name: "Europe/London", count: 487 },
    { name: "Asia/Tokyo", count: 392 },
    { name: "America/Los_Angeles", count: 289 },
    { name: "Europe/Paris", count: 234 },
    { name: "Asia/Shanghai", count: 187 },
  ],
  countryStats: [
    { name: "United States", count: 832 },
    { name: "United Kingdom", count: 487 },
    { name: "Japan", count: 392 },
    { name: "France", count: 234 },
    { name: "China", count: 187 },
    { name: "Germany", count: 156 },
  ],
});

const BusinessCardStatisticsModal: React.FC<
  BusinessCardStatisticsModalProps
> = ({ show, onClose, userIdentification, userName }) => {
  const { t } = useTranslation();
  const { customer_identification, user } = useAuth();
  const [selectedPeriod, setSelectedPeriod] = useState<number>(7);
  const [statisticsData, setStatisticsData] = useState<ProcessedStats>({
    totalViews: 0,
    mobileViews: 0,
    desktopViews: 0,
    browserStats: [],
    dailyViews: [],
    platformData: [],
    timezoneStats: [],
    countryStats: [],
  });
  const permissions = usePermissions(user);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const { mutate, isLoading } = useUpdateTracker();

  const advancedStatsBenefits = [
    {
      id: "platform-analytics",
      text: t("businessCardPage.statisticsModal.benefit1") as string,
    },
    {
      id: "browser-stats",
      text: t("businessCardPage.statisticsModal.benefit2") as string,
    },
    {
      id: "timezone-tracking",
      text: t("businessCardPage.statisticsModal.benefit3") as string,
    },
  ];

  const displayPlatformData = !permissions.hasFeatureAccess(
    "BUSINESS_CARDS_ADVANCED_STATS"
  )
    ? getMockData(t).platformData
    : statisticsData.platformData;

  const displayBrowserStats = !permissions.hasFeatureAccess(
    "BUSINESS_CARDS_ADVANCED_STATS"
  )
    ? getMockData(t).browserStats
    : statisticsData.browserStats;

  const displayTimezoneStats = !permissions.hasFeatureAccess(
    "BUSINESS_CARDS_ADVANCED_STATS"
  )
    ? getMockData(t).timezoneStats
    : statisticsData.timezoneStats;

  const displayCountryStats = !permissions.hasFeatureAccess(
    "BUSINESS_CARDS_ADVANCED_STATS"
  )
    ? getMockData(t).countryStats
    : statisticsData.countryStats;

  const processStatistics = useCallback(
    (events: BusinessCardEvent[]) => {
      if (!events || events.length === 0) {
        setStatisticsData({
          totalViews: 0,
          mobileViews: 0,
          desktopViews: 0,
          browserStats: [],
          dailyViews: [],
          platformData: [],
          timezoneStats: [],
          countryStats: [],
        });
        return;
      }

      const totalViews = events.length;

      const mobileViews = events.filter((e) => e.value?.value?.isMobile).length;
      const desktopViews = totalViews - mobileViews;

      const browserCount: Record<string, number> = {};
      events.forEach((event) => {
        const browserName =
          event.value?.value?.browserName || "Private/Unknown";
        browserCount[browserName] = (browserCount[browserName] || 0) + 1;
      });

      const browserStats = Object.entries(browserCount)
        .map(([name, count]) => ({ name, count }))
        .sort((a, b) => b.count - a.count);

      const dailyCount: Record<string, number> = {};
      events.forEach((event) => {
        const date = moment(event.date_time).format("MM/DD");
        dailyCount[date] = (dailyCount[date] || 0) + 1;
      });

      const dailyViews = Object.entries(dailyCount)
        .map(([date, views]) => ({ date, views }))
        .sort((a, b) => moment(a.date, "MM/DD").diff(moment(b.date, "MM/DD")));

      const platformData = [
        {
          name: t("businessCardPage.statisticsModal.mobile"),
          value: mobileViews,
        },
        {
          name: t("businessCardPage.statisticsModal.desktop"),
          value: desktopViews,
        },
      ];

      const timezoneCount: Record<string, number> = {};
      events.forEach((event) => {
        const timezone = event.value?.value?.timezone || "Private/Unknown";
        timezoneCount[timezone] = (timezoneCount[timezone] || 0) + 1;
      });

      const timezoneStats = Object.entries(timezoneCount)
        .map(([name, count]) => ({ name, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 10);

      const countryCount: Record<string, number> = {};
      events.forEach((event) => {
        const timezone = event.value?.value?.timezone;
        if (timezone && timezone !== "Private/Unknown") {
          const timezoneData = ct.getTimezone(timezone);
          if (timezoneData?.countries && timezoneData.countries.length > 0) {
            const countryCode = timezoneData.countries[0];
            const countryData = ct.getCountry(countryCode);
            const countryName = countryData?.name || countryCode;
            countryCount[countryName] = (countryCount[countryName] || 0) + 1;
          } else {
            countryCount["Private/Unknown"] =
              (countryCount["Private/Unknown"] || 0) + 1;
          }
        } else {
          countryCount["Private/Unknown"] =
            (countryCount["Private/Unknown"] || 0) + 1;
        }
      });

      const countryStats = Object.entries(countryCount)
        .map(([name, count]) => ({ name, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 10);

      setStatisticsData({
        totalViews,
        mobileViews,
        desktopViews,
        browserStats,
        dailyViews,
        platformData,
        timezoneStats,
        countryStats,
      });
    },
    [t]
  );

  const handleFetchStats = useCallback(
    (period: number) => {
      setSelectedPeriod(period);

      const toDate = moment().format("YYYY-MM-DD");
      const fromDate = moment().subtract(period, "days").format("YYYY-MM-DD");

      mutate(
        {
          customer_identification: customer_identification,
          user_identification: userIdentification,
          entity_identification: user.entity_identification,
          events: ["USER_BUSINESS_CARD_SCANNED"],
          from: fromDate,
          to: toDate,
        },
        {
          onSuccess: (data: AxiosResponse) => {
            const events: BusinessCardEvent[] = data?.data?.element || [];
            processStatistics(events);
          },
          onError: (error) => {
            toast.error(t("businessCardPage.statisticsModal.fetchError"));
          },
        }
      );
    },
    [
      customer_identification,
      userIdentification,
      user.entity_identification,
      mutate,
      processStatistics,
      t,
    ]
  );

  useEffect(() => {
    if (show && userIdentification) {
      handleFetchStats(selectedPeriod);
    }
  }, [show, userIdentification, handleFetchStats]);

  return (
    <>
      <ModalShell
        show={show}
        onClose={onClose}
        size="7xl"
        title={
          <span className="flex items-center gap-2">
            <HiOutlineChartBar className="text-blue-600 shrink-0" size={24} />
            {t("businessCardPage.statisticsModal.title")}
          </span>
        }
      >
        <div className="space-y-6">
          <div className="pb-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">{userName}</h3>
            <p className="text-sm text-gray-500">
              {t("businessCardPage.statisticsModal.subtitle")}
            </p>
          </div>

          <div className="flex flex-col gap-3">
            <div className="text-sm font-medium text-gray-700">
              {t("businessCardPage.statisticsModal.timePeriod")}
            </div>
            <div className="flex gap-2 flex-wrap">
              {[7, 30, 90].map((period: number) => (
                <CustomButton
                  key={period}
                  size="sm"
                  variant={period === selectedPeriod ? "primary" : "secondary"}
                  onClick={() => handleFetchStats(period)}
                  disabled={isLoading}
                  icon={
                    isLoading && period === selectedPeriod ? (
                      <span translate="no">
                        <Spinner size="sm" />
                      </span>
                    ) : undefined
                  }
                  title={t("businessCardPage.statisticsModal.lastDays", {
                    days: period,
                  })}
                />
              ))}
            </div>
          </div>

          {isLoading ? (
            <div className="flex justify-center items-center py-12">
              <div className="flex flex-col items-center gap-3">
                <Spinner size="lg" />
                <span className="text-sm text-gray-600">
                  {t("businessCardPage.statisticsModal.loading")}
                </span>
              </div>
            </div>
          ) : (
            <>
              {statisticsData.totalViews === 0 ? (
                <EmptyState
                  icon={<HiOutlineChartBar size={24} />}
                  title={t("businessCardPage.statisticsModal.noData")}
                  description={t(
                    "businessCardPage.statisticsModal.noDataDescription"
                  )}
                />
              ) : (
                <>
                  {permissions.hasFeatureAccess(
                    "BUSINESS_CARDS_STATS_VIEW"
                  ) && (
                    <div className="bg-white border border-gray-200 rounded-lg p-4">
                      <h4 className="text-sm font-medium text-gray-700 mb-4">
                        {t("businessCardPage.statisticsModal.dailyViews")}
                      </h4>
                      <ResponsiveContainer width="100%" height={250}>
                        <LineChart data={statisticsData.dailyViews}>
                          <CartesianGrid
                            strokeDasharray="3 3"
                            stroke={CHART.grid}
                          />
                          <XAxis
                            dataKey="date"
                            tick={{ fontSize: 12, fill: CHART.axis }}
                            axisLine={false}
                            tickLine={false}
                          />
                          <YAxis
                            tick={{ fontSize: 12, fill: CHART.axis }}
                            axisLine={false}
                            tickLine={false}
                            allowDecimals={false}
                          />
                          <Tooltip
                            contentStyle={{
                              backgroundColor: CHART.tooltipBg,
                              color: CHART.tooltipText,
                              border: "none",
                              borderRadius: "8px",
                            }}
                          />
                          <Line
                            type="monotone"
                            dataKey="views"
                            stroke={CHART.series}
                            strokeWidth={2}
                            dot={{ fill: CHART.series, r: 4 }}
                            activeDot={{ r: 6 }}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  )}

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
                    <div className="bg-white border border-gray-200 rounded-lg p-3 lg:p-4 relative overflow-hidden">
                      <h4 className="text-sm font-medium text-gray-700 mb-3">
                        {t(
                          "businessCardPage.statisticsModal.platformDistribution"
                        )}
                      </h4>
                      <div
                        className={
                          !permissions.hasFeatureAccess(
                            "BUSINESS_CARDS_ADVANCED_STATS"
                          )
                            ? "filter blur-sm pointer-events-none"
                            : ""
                        }
                      >
                        <div className="flex flex-col lg:flex-row items-center gap-4">
                          <ResponsiveContainer width="100%" height={260}>
                            <PieChart>
                              <Pie
                                data={displayPlatformData}
                                cx="50%"
                                cy="50%"
                                labelLine={false}
                                outerRadius={85}
                                fill={COLORS[0]}
                                dataKey="value"
                              >
                                {displayPlatformData.map((_, index) => (
                                  <Cell
                                    key={`cell-${index}`}
                                    fill={COLORS[index % COLORS.length]}
                                  />
                                ))}
                              </Pie>
                              <Tooltip
                                contentStyle={{
                                  backgroundColor: CHART.tooltipBg,
                                  color: CHART.tooltipText,
                                  border: "none",
                                  borderRadius: "8px",
                                }}
                                labelStyle={{
                                  color: CHART.tooltipText,
                                }}
                                itemStyle={{
                                  color: CHART.tooltipText,
                                }}
                              />
                            </PieChart>
                          </ResponsiveContainer>
                          <div className="flex flex-col gap-2">
                            {displayPlatformData.map((item, index) => {
                              const total = displayPlatformData.reduce(
                                (sum, d) => sum + d.value,
                                0
                              );
                              const percentage =
                                total > 0
                                  ? ((item.value / total) * 100).toFixed(1)
                                  : 0;
                              return (
                                <div
                                  key={index}
                                  className="flex items-center gap-2"
                                >
                                  <div
                                    className="w-3 h-3 rounded-sm flex-shrink-0"
                                    style={{
                                      backgroundColor:
                                        COLORS[index % COLORS.length],
                                    }}
                                  />
                                  <span
                                    className="text-xs text-gray-700"
                                    title={item.name}
                                  >
                                    {item.name}
                                  </span>
                                  <span className="text-xs font-medium text-gray-900 ml-auto">
                                    {percentage}%
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                      {!permissions.hasFeatureAccess(
                        "BUSINESS_CARDS_ADVANCED_STATS"
                      ) && (
                        <div
                          className="absolute inset-0 flex items-center justify-center bg-white/40 backdrop-blur-[2px] cursor-pointer"
                          onClick={() => setShowUpgradeModal(true)}
                        >
                          <div className="text-center">
                            <UnlockBadge className="mb-2" />
                          </div>
                        </div>
                      )}
                    </div>
                    {displayBrowserStats.length > 0 && (
                      <div className="bg-white border border-gray-200 rounded-lg p-3 lg:p-4 relative overflow-hidden">
                        <h4 className="text-sm font-medium text-gray-700 mb-3">
                          {t("businessCardPage.statisticsModal.browserStats")}
                        </h4>
                        <div
                          className={
                            !permissions.hasFeatureAccess(
                              "BUSINESS_CARDS_ADVANCED_STATS"
                            )
                              ? "filter blur-sm pointer-events-none"
                              : ""
                          }
                        >
                          <ResponsiveContainer width="100%" height={240}>
                            <BarChart data={displayBrowserStats}>
                              <CartesianGrid
                                strokeDasharray="3 3"
                                stroke={CHART.grid}
                              />
                              <XAxis
                                dataKey="name"
                                tick={{ fontSize: 12, fill: CHART.axis }}
                                axisLine={false}
                                tickLine={false}
                              />
                              <YAxis
                                tick={{ fontSize: 12, fill: CHART.axis }}
                                axisLine={false}
                                tickLine={false}
                                allowDecimals={false}
                              />
                              <Tooltip
                                contentStyle={{
                                  backgroundColor: CHART.tooltipBg,
                                  color: CHART.tooltipText,
                                  border: "none",
                                  borderRadius: "8px",
                                }}
                                labelStyle={{
                                  color: CHART.tooltipText,
                                }}
                                itemStyle={{
                                  color: CHART.tooltipText,
                                }}
                              />
                              <Bar
                                dataKey="count"
                                fill={CHART.series}
                                radius={[8, 8, 0, 0]}
                              />
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                        {!permissions.hasFeatureAccess(
                          "BUSINESS_CARDS_ADVANCED_STATS"
                        ) && (
                          <div
                            className="absolute inset-0 flex items-center justify-center bg-white/40 backdrop-blur-[2px] cursor-pointer"
                            onClick={() => setShowUpgradeModal(true)}
                          >
                            <div className="text-center">
                              <UnlockBadge className="mb-2" />
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                    {displayTimezoneStats.length > 0 && (
                      <div className="bg-white border border-gray-200 rounded-lg p-3 lg:p-4 relative overflow-hidden">
                        <h4 className="text-sm font-medium text-gray-700 mb-3">
                          {t("businessCardPage.statisticsModal.timezoneStats")}
                        </h4>
                        <div
                          className={
                            !permissions.hasFeatureAccess(
                              "BUSINESS_CARDS_ADVANCED_STATS"
                            )
                              ? "filter blur-sm pointer-events-none"
                              : ""
                          }
                        >
                          <div className="flex flex-col lg:flex-row items-center gap-4">
                            <ResponsiveContainer width="100%" height={260}>
                              <PieChart>
                                <Pie
                                  data={displayTimezoneStats.map((item) => ({
                                    name: item.name,
                                    value: item.count,
                                  }))}
                                  cx="50%"
                                  cy="50%"
                                  labelLine={false}
                                  outerRadius={85}
                                  fill={COLORS[0]}
                                  dataKey="value"
                                >
                                  {displayTimezoneStats.map((_item, index) => (
                                    <Cell
                                      key={`cell-${index}`}
                                      fill={COLORS[index % COLORS.length]}
                                    />
                                  ))}
                                </Pie>
                                <Tooltip
                                  contentStyle={{
                                    backgroundColor: CHART.tooltipBg,
                                    color: CHART.tooltipText,
                                    border: "none",
                                    borderRadius: "8px",
                                  }}
                                  labelStyle={{
                                    color: CHART.tooltipText,
                                  }}
                                  itemStyle={{
                                    color: CHART.tooltipText,
                                  }}
                                />
                              </PieChart>
                            </ResponsiveContainer>
                            <div className="flex flex-col gap-2">
                              {displayTimezoneStats.map((item, index) => {
                                const total = displayTimezoneStats.reduce(
                                  (sum, d) => sum + d.count,
                                  0
                                );
                                const percentage =
                                  total > 0
                                    ? ((item.count / total) * 100).toFixed(1)
                                    : 0;
                                return (
                                  <div
                                    key={index}
                                    className="flex items-center gap-2"
                                  >
                                    <div
                                      className="w-3 h-3 rounded-sm flex-shrink-0"
                                      style={{
                                        backgroundColor:
                                          COLORS[index % COLORS.length],
                                      }}
                                    />
                                    <span
                                      className="text-xs text-gray-700"
                                      title={item.name}
                                    >
                                      {item.name}
                                    </span>
                                    <span className="text-xs font-medium text-gray-900 ml-auto">
                                      {percentage}%
                                    </span>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                        {!permissions.hasFeatureAccess(
                          "BUSINESS_CARDS_ADVANCED_STATS"
                        ) && (
                          <div
                            className="absolute inset-0 flex items-center justify-center bg-white/40 backdrop-blur-[2px] cursor-pointer"
                            onClick={() => setShowUpgradeModal(true)}
                          >
                            <div className="text-center">
                              <UnlockBadge className="mb-2" />
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {displayCountryStats.length > 0 && (
                      <div className="bg-white border border-gray-200 rounded-lg p-3 lg:p-4 relative overflow-hidden">
                        <h4 className="text-sm font-medium text-gray-700 mb-3">
                          {t("businessCardPage.statisticsModal.countryStats")}
                        </h4>
                        <div
                          className={
                            !permissions.hasFeatureAccess(
                              "BUSINESS_CARDS_ADVANCED_STATS"
                            )
                              ? "filter blur-sm pointer-events-none"
                              : ""
                          }
                        >
                          <div className="flex flex-col lg:flex-row items-center gap-4">
                            <ResponsiveContainer width="100%" height={260}>
                              <PieChart>
                                <Pie
                                  data={displayCountryStats.map((item) => ({
                                    name: item.name,
                                    value: item.count,
                                  }))}
                                  cx="50%"
                                  cy="50%"
                                  labelLine={false}
                                  outerRadius={85}
                                  fill={COLORS[0]}
                                  dataKey="value"
                                >
                                  {displayCountryStats.map((_item, index) => (
                                    <Cell
                                      key={`cell-${index}`}
                                      fill={COLORS[index % COLORS.length]}
                                    />
                                  ))}
                                </Pie>
                                <Tooltip
                                  contentStyle={{
                                    backgroundColor: CHART.tooltipBg,
                                    color: CHART.tooltipText,
                                    border: "none",
                                    borderRadius: "8px",
                                  }}
                                  labelStyle={{
                                    color: CHART.tooltipText,
                                  }}
                                  itemStyle={{
                                    color: CHART.tooltipText,
                                  }}
                                />
                              </PieChart>
                            </ResponsiveContainer>
                            <div className="flex flex-col gap-2">
                              {displayCountryStats.map((item, index) => {
                                const total = displayCountryStats.reduce(
                                  (sum, d) => sum + d.count,
                                  0
                                );
                                const percentage =
                                  total > 0
                                    ? ((item.count / total) * 100).toFixed(1)
                                    : 0;
                                return (
                                  <div
                                    key={index}
                                    className="flex items-center gap-2"
                                  >
                                    <div
                                      className="w-3 h-3 rounded-sm flex-shrink-0"
                                      style={{
                                        backgroundColor:
                                          COLORS[index % COLORS.length],
                                      }}
                                    />
                                    <span
                                      className="text-xs text-gray-700"
                                      title={item.name}
                                    >
                                      {item.name}
                                    </span>
                                    <span className="text-xs font-medium text-gray-900 ml-auto">
                                      {percentage}%
                                    </span>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                        {!permissions.hasFeatureAccess(
                          "BUSINESS_CARDS_ADVANCED_STATS"
                        ) && (
                          <div
                            className="absolute inset-0 flex items-center justify-center bg-white/40 backdrop-blur-[2px] cursor-pointer"
                            onClick={() => setShowUpgradeModal(true)}
                          >
                            <div className="text-center">
                              <UnlockBadge className="mb-2" />
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </>
              )}
            </>
          )}

          <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
            <p className="text-sm text-gray-600">
              <strong>{t("businessCardPage.statisticsModal.note")}:</strong>{" "}
              {t("businessCardPage.statisticsModal.noteDescription")}
            </p>
          </div>
        </div>
      </ModalShell>

      <UpgradeModal
        showModal={showUpgradeModal}
        setShowModal={setShowUpgradeModal}
        targetPlan={PlanTier.LARGE_BUSINESS}
        title={
          t("businessCardPage.statisticsModal.advancedStatsTitle") as string
        }
        description={
          t(
            "businessCardPage.statisticsModal.advancedStatsDescription"
          ) as string
        }
        benefits={advancedStatsBenefits}
        icon={HiOutlineChartBar}
        iconTheme="yellow"
        upgradeText={
          t("businessCardPage.statisticsModal.upgradeText") as string
        }
      />
    </>
  );
};

export default BusinessCardStatisticsModal;
